import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "../../store"
import { fetchAllBookHalls, fetchAllBookStays, fetchBookingsCount, filterAdminHalls, filterAdminPlaces } from "../../features/admin/adminbookingSlice"
import { useTranslation } from "react-i18next"
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts"
import { TrendingUp, Calendar, DollarSign } from "lucide-react"
import { FiSearch } from "react-icons/fi"
import { FiCreditCard, FiCalendar } from "react-icons/fi"

import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import { getStatusClass } from "../../utils/data"
import { Link } from "react-router-dom"
import ScrollButton from "../ui/ScrollButton"
import Pagination from "../ui/Pagination"
import dayjs from "dayjs"
import InnerLoading from "../ui/InnerLoading"
const AdminCard = ({ title, icon: Icon, children }: any) => {
    return (
        <div className="bg-white rounded-2xl shadow-md p-5 flex flex-col gap-4 w-full">
            <div className="flex items-center justify-between border-b pb-2">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                    <Icon className="w-5 h-5 text-gray-500" />
                    {title}
                </h3>
            </div>
            <div>{children}</div>
        </div>
    )
}

const Books = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch<AppDispatch>()
    const { stayBookings, filterBookingResult, hallbookings, pagination, bookingsCount ,loading} = useSelector((state: RootState) => state.adminBookingSlice)
    const [typeFilter, setTypeFilter] = useState<"Stay" | "Hall">("Stay")
    const [isFiltered, setIsFiltered] = useState(false)
    const [bookId, setBookId] = useState<number>()
    const [statusFilter, setStatusFilter] = useState("all")
    const [checkInFilter, setCheckInFilter] = useState<Date | null>(null)
    const [checkOutFilter, setCheckOutFilter] = useState<Date | null>(null)
    let displayedBookings =
        isFiltered
            ? filterBookingResult || []
            : typeFilter === "Stay"
                ? stayBookings
                : hallbookings
    useEffect(() => {
        dispatch(fetchBookingsCount())
        if (typeFilter === "Stay") {
            dispatch(fetchAllBookStays(pagination?.currentPage || 1))
        } else {
            dispatch(fetchAllBookHalls(pagination?.currentPage || 1))
        }
    }, [dispatch, pagination?.currentPage, typeFilter])



    const pieData = [
        { name: t("upcoming"), value: bookingsCount?.upcomingCount },
        { name: t("completed"), value: bookingsCount?.expiredCount },
    ]
    const statusCounts = displayedBookings.reduce((acc: any, b: any) => {
        acc[b.state] = (acc[b.state] || 0) + 1
        return acc
    }, {})

    const statusData = Object.keys(statusCounts).map((state) => ({
        name: t(state),
        value: statusCounts[state],
    }))
    const COLORS = ["#bfc6cc", "#007118"]
    const STATUS_COLORS = ["#b91c1c", "#007118", "#f59e0b", "#3b82f6", "#8b5cf6"]






    const handleFilterSubmit = (page = pagination?.currentPage || 1) => {
        const stayPayload = {
            type: typeFilter,
            bookId: bookId || undefined,
            status: statusFilter !== "all" ? statusFilter : undefined,
            checkIn: checkInFilter ? checkInFilter.toISOString() : undefined,
            checkOut: checkOutFilter ? checkOutFilter.toISOString() : undefined,
        }
        const hallPayload = {
            type: typeFilter,
            bookId: bookId || undefined,
            status: statusFilter !== "all" ? statusFilter : undefined,
            fromDate: checkInFilter ? checkInFilter.toISOString() : undefined,
            toDate: checkOutFilter ? checkOutFilter.toISOString() : undefined,
        }

        setIsFiltered(true)
        dispatch(
            typeFilter==="Hall"? 
            filterAdminHalls({
                placeData: hallPayload,
                page,
            })
            :filterAdminPlaces({
                placeData: stayPayload,
                page,
            })
           
        )
    }
    
    return (
        <div>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl shadow-md p-6 mb-6">
                <h2 className="text-white text-lg font-semibold">{t("books")}</h2>
                <p className="text-sm opacity-80 mt-1">
                    {t("total-bookings")}: {displayedBookings?.length}/{pagination?.totalObjects}
                </p>
            </div>


            {/* Analytics */}
            <div className=" grid md:grid-cols-3 mb-5 gap-6 w-full">
                <AdminCard title={t("totalBookings")} icon={TrendingUp}>
                    <p className="text-2xl font-bold text-gray-900">{pagination?.totalObjects}</p>
                </AdminCard>

                <AdminCard title={t("upcoming")} icon={Calendar}>
                    <p className="text-2xl font-bold text-blue-600">{bookingsCount?.upcomingCount}</p>
                </AdminCard>

                <AdminCard title={t("revenue")} icon={DollarSign}>
                    <p className="text-2xl font-bold text-primary">
                        LYD {bookingsCount?.benefits}
                    </p>
                </AdminCard>
            </div>

            {/* Chart */}
            <AdminCard title={t("bookingsOverview")} icon={TrendingUp}>
                <div className=" grid xs:grid-cols-1 sm:grid-cols-2">
                    <div className="h-60 ">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="h-60">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={statusData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label
                                >
                                    {statusData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={STATUS_COLORS[index % STATUS_COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

            </AdminCard>
            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm p-4 my-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">

                <div className="relative flex-1 min-w-[200px]">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="number"
                        placeholder={t("search-user") as string}
                        value={bookId}
                        onChange={(e) => setBookId(Number(e.target.value))}
                        className="pl-10 pr-3 py-2 border rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>
                <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as "Stay" | "Hall")}
                    className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                    <option value="Stay">{t("stay")}</option>
                    <option value="Hall">{t("hall")}</option>
                </select>

                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                    <option value="all">{t("All Statuses")}</option>
                    <option value="Pending">{t("pending-cash") + "/" + t("pending")}</option>
                    <option value="Approved">{t("approved")}</option>
                    <option value="Returned">{t("returned")}</option>
                    <option value="Pre-Return">{t("pre-Return")}</option>
                    <option value="Cancelled">{t("cancelled")}</option>
                    <option value="Paid">{t("paid")}</option>
                    <option value="Waiting">{t("waiting")}</option>
                    <option value="CancelledByHotel">{t("cancelledbyhotel")}</option>
                </select>

                <DatePicker
                    selected={checkInFilter}
                    onChange={(date) => setCheckInFilter(date)}
                    placeholderText={t("check-in") as string}
                    className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    dateFormat="yyyy-MM-dd"
                />
                <DatePicker
                    selected={checkOutFilter}
                    onChange={(date) => setCheckOutFilter(date)}
                    placeholderText={t("check-out") as string}
                    className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    dateFormat="yyyy-MM-dd"
                />

                <button
                    onClick={() => handleFilterSubmit(1)}
                    className="bg-primary hover:bg-[#00470f] text-white px-4 py-2 rounded-lg text-sm"
                >
                    {t("search")}
                </button>
            </div>
            <div className="grid rounded-xl bg-white mt-5 p-4 gap-6 w-full">
                {
                displayedBookings?.length===0? <div className="w-full flex-center flex-col text-center mt-20">
                <img src="/asset/empty.png" alt="" className="w-48 mb-4" />
                <p className="text-gray-600">{t("no-bookings")}</p>
            </div>: 
            loading ? <InnerLoading/>   :
                displayedBookings.map((booking, index) => (
                    <div key={index} className="flex items-center justify-between bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200">

                        <div className="flex items-center gap-4">
                            <img
                                src={booking.place?.images?.[0]?.url || "/asset/modern-commercial-building.webp"}
                                alt={booking.place?.title}
                                className="w-16 h-16 rounded-lg object-cover"
                            />
                            <div>
                                <Link to={booking?.place?.type === "Stay" ? `/dashboard/stay-booking-report/${booking.place.id}` : `/dashboard/hall-booking-report/${booking?.place?.id}`}
                                    className="font-semibold text-gray-800 dark:text-gray-100">
                                    {booking.place?.title || t("unknownPlace")}
                                </Link>

                                <p className="text-xs text-textSecond">
                                    {t("bookingId")}: #{booking.id}
                                </p>
                                {booking?.rooms?.length > 0 &&
                                    <p className="text-xs text-textSecond">
                                        {t("room-number")}: {booking.rooms.map(room => room.roomNumber).join("، ")}
                                    </p>
                                }
                                <p className="text-xs text-textSecond">
                                    {t("creared-at")}:{dayjs(booking.createAt).format('YYYY-MM-DD')}
                                </p>
                                <Link to={`/dashboard/user-booking-report/${booking.userId}`} className="text-xs text-textSecond">
                                    {t("user")}: {booking.userName}
                                </Link>
                                {
                                    booking.place.type==="Hall"?
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                    <FiCalendar /> { booking?.bookDate&&new Date(booking?.bookDate).toLocaleDateString()} →{" "}
                                    {booking.duration===1?t("morning-period"):t("afternoon-period")}
                                </p>
                                    :
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                    <FiCalendar /> {new Date(booking.checkIn).toLocaleDateString()} →{" "}
                                    {new Date(booking.checkOut).toLocaleDateString()}
                                </p>
                                }
                                
                            </div>
                        </div>

                        <div className="flex flex-col text-right" >
                            <span
                                className={`px-3 py-1 rounded-full text-xs font-medium self-end ${getStatusClass(
                                    booking.state
                                )}`}
                            >
                                {booking?.state === "Pending" && booking.paymentMethod === "Cash" ? t("pending-cash") : t(`${booking?.state.toLowerCase()}`) || booking?.state}
                            </span>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 flex items-center gap-1">
                                <FiCreditCard /> {booking.currency} {booking.price.toFixed(2)}
                            </p>
                        </div>

                        <Link
                            className="ml-4 px-4 py-2 text-sm bg-primary hover:bg-[#004e11] text-white rounded-lg" to={booking.place.type==="Hall"?`/dashboard/hall-book-details/${booking.id}`: `/dashboard/book-details/${booking.id}`}                        >
                            {t("view")}
                        </Link>
                    </div>
                ))
                }
                <Pagination
                    currentPage={pagination?.currentPage || 1}
                    lastPage={pagination?.lastPage || 1}
                    onPageChange={(page) => handleFilterSubmit(page)}
                />
            </div >
            <ScrollButton />
        </div >
    )
}

export default Books
